import liff from '@line/liff';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { Button } from '@/components/common/Button';
import Table from '@/components/table/Table';
import { ColumnDef } from '@tanstack/react-table';
import { BodyGetInvoices, File } from '@/types/files';
import { format } from 'date-fns';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import iconAddFriend from '../../assets/images/add-friend-white.png';
import iconCalendar from '../../assets/images/calendar.png';
import { useLocation } from 'react-router-dom';
import { getStorage, removeStorage, saveLocalStorage } from '@/utils/browsers';
import { keyStorage } from '@/constant/string';
import { Download } from 'lucide-react';
import { ja } from 'date-fns/locale';
import { t } from 'i18next';
import { Customer } from '@/types/auth';
import { flowResult, toJS } from 'mobx';
import { AuthApi } from '@/apis';
import './index.scss';
import logo from '../../assets/images/logo_red.png';

export default observer(function HomePage() {
    const location = useLocation();
    const { checkIdLine } = location.state || {};
    const {
        modalStore: { showAlertModal, hideModal, showErrorModal },
        filesStore: { listInvoices, getListInvoices, keepSearchParams, setObservable, checkInvoiceStatus },
        authStore: { logout, setProfile, profile },
        userStore: { getMyInfo }
    } = useStore();
    const [params, setParams] = useState<BodyGetInvoices>(Object.keys(keepSearchParams).length > 0 ?
        { ...keepSearchParams } :
        {
            upload_month: (new Date().getMonth() + 1).toString(),
            upload_year: new Date().getFullYear().toString()
        });
    const [openDatePicker, setOpenDatePicke] = useState<boolean>(false);
    useEffect(() => {
        const handlePopState = () => {
            window.history.pushState(null, '', window.location.href);
            liff.closeWindow();
        };
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        if (checkIdLine) {
            setTimeout(() => {
                checkUserId();
            }, 2500);
        }
    }, [checkIdLine]);
    useEffect(() => {
        fetchListInvoices(params);
        checkFriendStatus();
    }, []);

    const checkFriendStatus = () => {
        if (liff.isApiAvailable('scanCodeV2')) {
            liff.getFriendship().then((data) => {
                if (!data.friendFlag) {
                    displayPopupAddFriend();
                }
            });
        } else {
            setTimeout(() => {
                checkFriendStatus();
            }, 1000);
        }
    };

    const checkUserId = async () => {
        const profile: Customer | undefined = await flowResult(getMyInfo());
        if (!liff) {
            setTimeout(() => {
                checkUserId();
                return;
            }, 2500);
        }
        if (!profile) {
            return;
        }
        setProfile(profile);
        const id = liff.getIDToken();
        const lineProfile: any = await new AuthApi().getProfileLine(id);
        if (lineProfile && lineProfile.sub) {
            const line_id = lineProfile.sub;
            if (profile?.line_id !== line_id) {
                showErrorModal({
                    content: t('messages.account_changed')
                });
                setTimeout(() => {
                    logout();
                }, 2500);
            }
        }
    };

    const fetchListInvoices = async (payload = params) => {
        setSelectedRows([]);
        await getListInvoices(payload);
    };

    const displayPopupAddFriend = () => {
        liff.getFriendship().then((data) => {
            if (!data.friendFlag) {
                showAlertModal({
                    id: 'add-friend',
                    title: t('title.add_friend'),
                    cancelButton: false,
                    saveButton: false,
                    showCloseIcon: false,
                    content: (
                        <div className='w-full text-start'>
                            <p className='mt-6 text-[14px] text-black text-left'>・本サービスのご利用には、公式LINEアカウントの友達登録が必要です。</p>
                            <p className='mt-6 text-[14px] text-main text-center'>友達登録の後に再度、カメラを起動してQRコードの読み込みをしてください。</p>
                            <div className='flex flex-row text-center mt-8 w-full justify-center'>
                                <Button
                                    onClick={async () => {
                                        hideModal();
                                        handleAddFriend(true);
                                    }}
                                    className='min-w-32 bg-main'
                                >
                                    {t('text.agree')}
                                </Button>
                            </div>
                        </div>
                    )
                });
            }
        });
    };

    const handleAddFriend = async (forceLogout = false) => {
        const newProfile = await flowResult(getMyInfo());
        if (!newProfile) {
            showErrorModal({
                content: t('messages.account_invalid')
            });
            return;
        }
        const link = newProfile?.line_profile_link || undefined;
        const regex = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/[^\s]*)?$/;
        if (!liff || !liff.isInClient()) {
            showErrorModal({
                content: t('messages.liff_error')
            });
            return;
        }
        if (!link || !regex.test(link)) {
            showErrorModal({
                content: t('messages.profile_invalid')
            });
            return;
        }
        if (link !== profile?.line_profile_link) {
            saveLocalStorage(keyStorage.LINE_PROFILE_LINK, link);
            setProfile({ ...profile, line_profile_link: link });
        }
        if (forceLogout) {
            removeStorage([keyStorage.TOKEN, keyStorage.PROFILE_USER, keyStorage.LINE_PROFILE_LINK, keyStorage.UUID, keyStorage.LIFF_ID]);
        }
        liff.openWindow({
            url: link,
            external: false
        });
    };

    const handleOpenFile = (path: string) => {
        if (!liff || !liff.isInClient()) {
            alert(t('messages.not_in_Line'));
            return;
        }
        if (!path) {
            alert(t('messages.error'));
            return;
        }
        const url = `https://drive.google.com/open?id=${path}`;
        try {
            liff.openWindow({
                url: url,
                external: false
            });
        } catch (error) {
            alert(t('messages.error'));
        }
    };

    const [selectedRows, setSelectedRows] = useState<File[]>([]);

    const columns: ColumnDef<File>[] = [
        {
            header: ' ',
            size: 250,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='text-center w-full'>
                    <p className=' line-clamp-1'>
                        {format(original.created_at!, 'yyyy/MM/dd')}
                    </p>
                    <p className=' line-clamp-1'>
                        {format(original.created_at!, 'HH:mm:ss')}
                    </p>
                </div>

            )
        },
        {
            header: ' ',
            size: 500,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div
                    onClick={() => {
                        handleOpenFile(original.path);
                    }}
                    className='cursor-pointer text-[15px] text-blue-600 underline text-left line-clamp-2'
                >
                    {original.name}
                </div>
            )
        },
        {
            header: ' ',
            size: 10,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <Download
                    size={20}
                    className='cursor-pointer'
                    onClick={async () => {
                        const token = getStorage(keyStorage.TOKEN);
                        const checkRes = await flowResult(checkInvoiceStatus({ ids: [original.id], token: token }));
                        if (!checkRes) {
                            toastify('error', t('messages.invalid_files'));
                            fetchListInvoices(params);
                            return;
                        }
                        const data = {
                            id: original.id,
                            name: original.name,
                            path: original.path
                        };
                        const liff_id = getStorage(keyStorage.LIFF_ID);
                        const urlCallback = `https://liff.line.me/${liff_id}?liff_id=${liff_id}`;
                        const queryString = encodeURIComponent(JSON.stringify([data]));
                        const urlCallbackEncode = encodeURIComponent(urlCallback);
                        const tokenEncode = encodeURIComponent(token);
                        const url = `${window.location.origin}/download?items=${queryString}&callback=${liff_id ? urlCallbackEncode : window.location.origin}&token=${tokenEncode}`;
                        liff.openWindow({
                            url: url,
                            external: true
                        });
                    }}
                />
            )
        }
    ];

    const handleDownload = async () => {
        if (!selectedRows || selectedRows.length <= 0) {
            return;
        }
        const token = getStorage(keyStorage.TOKEN);
        const listIds = selectedRows.map(e => (e.id));
        const checkRes = await flowResult(checkInvoiceStatus({ ids: listIds, token: token }));
        if (!checkRes) {
            toastify('error', t('messages.invalid_files'));
            fetchListInvoices(params);
            return;
        }
        const dataDownload = selectedRows.map(e => ({
            id: e.id,
            name: e.name,
            path: e.path
        }));
        const platform = liff.getOS();
        const liff_id = getStorage(keyStorage.LIFF_ID);
        const urlCallback = `https://liff.line.me/${liff_id}?liff_id=${liff_id}`;
        const queryString = encodeURIComponent(JSON.stringify(dataDownload));
        const urlCallbackEncode = encodeURIComponent(urlCallback);
        const tokenEncode = encodeURIComponent(token);
        const platformEncode = encodeURIComponent(platform || '');
        const url = `${window.location.origin}/download?items=${queryString}&callback=${liff_id ? urlCallbackEncode : window.location.origin}&token=${tokenEncode}&platform=${platformEncode}`;
        liff.openWindow({
            url: url,
            external: true
        });
    };

    return (
        <div className='w-full h-full min-w-[300px]'>
            <div className='bg-white'>
                <div className='flex justify-center items-center content-center w-full p-4'>
                    <div
                        className='text-white flex justify-center text-4xl font-bold'
                    >
                        <img src={logo} className='w-[60%]' alt={logo} />
                    </div>
                    <div onClick={() => handleAddFriend()} className='bg-main hover:bg-main cursor-pointer rounded-full w-[40px] h-[40px] flex justify-center items-center absolute right-[16px]'>
                        <img src={iconAddFriend} alt={iconAddFriend} />
                    </div>
                </div>
            </div>
            <div className='px-[16px] py-[24px]'>
                <div className='flex justify-between items-center xs:flex-col'>
                    <DropdownMenu.Root open={openDatePicker} onOpenChange={setOpenDatePicke}>
                        <DropdownMenu.Trigger className='min-w-[150px] w-[40vw] h-[52px] px-4 py-1 flex justify-between items-center bg-white rounded-md text-[13px]'>
                            <p>{params.upload_year + '/' + ((parseInt(params.upload_month) < 10) ? '0' : '') + params.upload_month}</p>
                            <img src={iconCalendar} className='' alt={iconCalendar} />
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content className=''>
                                <DatePicker
                                    selected={new Date(parseInt(params.upload_year), parseInt(params.upload_month) - 1)}
                                    selectsRange={true}
                                    dateFormat='MMM yyy'
                                    showMonthYearPicker
                                    inline
                                    onSelect={(date) => {
                                        if (!date) return;
                                        const param = {
                                            upload_month: `${(date.getMonth() + 1)}`,
                                            upload_year: `${date.getFullYear()}`
                                        };
                                        setParams(param);
                                        setObservable('keepSearchParams', toJS(param), { isMergeObject: false });
                                        setOpenDatePicke(false);
                                        fetchListInvoices(param);
                                    }}
                                    locale={ja}
                                />
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                    {((selectedRows && selectedRows.length > 0) &&
                        (
                            <button
                                className='bg-main px-2 h-10 rounded-md text-white hover:bg-main cursor-pointer'
                                onClick={handleDownload}
                            >
                                {t('text.download')}
                            </button>
                        )
                    )}
                </div>
                <Table
                    columns={columns}
                    data={listInvoices}
                    getRowId={row => row.id.toString()}
                    containerClassName='mt-4 overflow-y-scroll max-h-[79vh]'
                    headerClassName='h-0'
                    showSelectPageSize={false}
                    showPagination={false}
                    showPagingInfo={false}
                    noData={t('messages.no_invoice')}
                    cellClassName='p-2 justify-center items-center content-center'
                    showTableFooter={false}
                />
            </div>
        </div>
    );
});
